import _, {isArray, isFunction, isInteger, pickBy} from 'lodash';
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';

import {globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {addQueryToUrl} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {useConstant} from '../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ErrorWithData} from '../../../../libs/shared/helpers/src/types/customErrors';
import {isPromise} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  buildCancelTokenSource,
  CancelTokenSource,
} from '../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {
  ApplicationMessageTypesEnum,
  ApplicationResultsMessage,
} from '../../../applications/bridge/v2/applicationBridgeV2';
import {ApplicationContext} from '../../../applications/bridge/v2/types/bridgeTypesV2';
import {IOTSError, readApiModel} from '../../../helpers/apiHelpers';
import {
  appIframePermissions,
  buildAppIframeFeaturePolicies,
} from '../../../helpers/models/applicationHelpers';
import {ApplicationEntryPointModel} from '../../../models/api/applicationEntryPointModel';
import {ApplicationModel} from '../../../models/api/applicationModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {
  ApiApplicationMessage,
  applicationContextFunctionArities,
  ApplicationMessageModel,
} from '../../../models/applications/applicationMessageModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {isEntrypointUrlValid} from '../../../settings/developers/pages/application/features/entryPoints/entryPointHelpers';
import {Dispatch} from '../../../store/appStore';
import {fetchApplicationEntryPointAuthenticationStatusAction} from '../../../store/data/applications/applicationsActions';
import {NewPanelId} from '../../../store/panels/panelStateBase';
import {Iframe} from '../../common/iframe';
import {buildBaseContext, ContextBuilder} from './applicationContextBase';

/*
 * Constants.
 */

const webViewBridgeHandshake = '@frontapp/ui-sdk#WebViewBridge.handshake';

/*
 * Props.
 */

interface ApplicationContextHostProps {
  dispatch: Dispatch;
  panelId: NewPanelId;
  user: TeammateModel;
  application: ApplicationModel;
  entryPoint: ApplicationEntryPointModel;
  contextBuilder: ContextBuilder;
}

/*
 * Component.
 */

export const ApplicationContextHost: FC<ApplicationContextHostProps> = (props) => {
  const {dispatch, panelId, user, entryPoint, contextBuilder, application} = props;
  const messageContexts = useConstant(() => new Map<string, ApplicationContext>());
  const contextFunctionCancelTokenSources = useConstant(() => new Map<string, CancelTokenSource>());
  const [iframePort, setIframePort] = useState<MessagePort>();
  const {applicationId, action, applicationUid, authentication} = entryPoint;

  useInitialEffect(() => {
    if (action?.origin) {
      dispatch(fetchApplicationEntryPointAuthenticationStatusAction(entryPoint));
    }
  });

  /*
   * Application context.
   */

  const baseContext = useMemo(
    () =>
      buildBaseContext({
        dispatch,
        user,
        applicationId,
        preferences: action || {},
        origin: action?.origin,
        panelId,
        authentication: authentication || {},
      }),
    [panelId, applicationId, dispatch, action, user, authentication],
  );

  const currentContext = useMemo(
    () => contextBuilder(baseContext, dispatch),
    [baseContext, dispatch, contextBuilder],
  );

  const sendMessageToPort = useCallback(
    (message: ApplicationResultsMessage) => {
      if (!iframePort) {
        return;
      }
      iframePort.postMessage(message);
    },
    [iframePort],
  );

  const trackApplicationFunctionEvent = useCallback(
    (eventName: any, eventFunctionData: any) => {
      const parameters = eventFunctionData.filter((data: any) => {
        // If the data is an array, we can keep it around.
        if (isArray(data)) {
          return true;
        }
        // Filter out undefined data.
        if (typeof data === 'undefined') {
          return false;
        }
        // Do not record objects.
        return typeof data !== 'object';
      });
      trackInterfaceEvent(eventName, {
        application_uid: applicationUid,
        entry_point_type: entryPoint?.type,
        parameters,
      });
    },
    [applicationUid, entryPoint?.type],
  );

  const trackApplicationContextEvent = useCallback(
    (id: string | undefined, type: string) => {
      // Make sure the iframe port is defined before we track anything.
      if (!iframePort) {
        return;
      }
      trackInterfaceEvent(InterfaceEventsEnum.APPLICATION_CONTEXT_UPDATE, {
        application_uid: applicationUid,
        entry_point_type: entryPoint?.type,
        context_type: type,
        context_id: id,
      });
    },
    [applicationUid, entryPoint?.type, iframePort],
  );

  const sendContext = useCallback(
    (context: ApplicationContext) => {
      // Keep track of the context for return calls.
      const contextId = context.id;
      messageContexts.set(contextId, context);

      const functionArities = _(context)
        .mapValues((v, k) => isFunction(v) && applicationContextFunctionArities[k])
        .pickBy(isInteger)
        .value();

      trackApplicationContextEvent(
        context.type === 'singleConversation' ? context.conversation.id : undefined,
        context.type,
      );

      // Send the context to the port. Remove the hostId and entryPointId from the context
      // before sending it.
      sendMessageToPort({
        type: ApplicationMessageTypesEnum.CONTEXT_UPDATE,
        context: {
          ...pickBy(context, (p) => !isFunction(p) && p !== 'hostId' && p !== 'entryPointId'),
          functionArities,
        } as ApplicationContext,
      });

      // Return a function to unregister the context.
      return () => {
        // Keep the context alive for a few ms in case the application already sent a request for it.
        globalWindow.setInstrumentedTimeout(() => messageContexts.delete(contextId), 250);
      };
    },
    [sendMessageToPort, trackApplicationContextEvent],
  );

  const sendResult = (message: ApplicationMessageModel, result: unknown) =>
    sendMessageToPort({
      id: message.id,
      type: ApplicationMessageTypesEnum.FUNCTION_RESULT,
      value: result,
    });

  const sendError = (message: ApplicationMessageModel, error: unknown) =>
    sendMessageToPort({
      id: message.id,
      type: ApplicationMessageTypesEnum.FUNCTION_ERROR,
      error: String(error),
    });

  /*
   * Listen for events on our window.
   */

  const onMessageFromIframe = (event: MessageEvent) => {
    // Only handle handshake messages from the iframe.
    if (event.data !== webViewBridgeHandshake || event.ports.length === 0) {
      return;
    }

    // Stop propagating the event.
    event.stopImmediatePropagation();

    const [port] = event.ports;
    setIframePort(port);
    sendContext(currentContext);
  };

  const onPortMessage: HandlerOf<MessageEvent> = useCallback(
    async (event) => {
      try {
        // Parse the message. If this fails, there's nothing we can do.
        const message = readApiModel(ApiApplicationMessage, event.data);
        switch (message.type) {
          case ApplicationMessageTypesEnum.FUNCTION_CALL: {
            const newCancelTokenSource = buildCancelTokenSource();
            contextFunctionCancelTokenSources.set(message.id, newCancelTokenSource);
            try {
              // Find the definition of the requested function.
              const functionDefinition = _(message.call).toPairs().first();

              if (!functionDefinition || !isArray(functionDefinition[1])) {
                throw new Error('No function definition was provided.');
              }

              const messageContext = messageContexts.get(message.contextId);
              if (!messageContext) {
                throw new Error('No context found with this ID.');
              }

              // At this point, we have to trust that the function exists.
              const [, functionRawArguments] = functionDefinition;
              const functionArguments = [...functionRawArguments.slice(0, -1), newCancelTokenSource.token];

              // We need to cast this as "any" since the interface event function is expecting a set of enums
              // but just pulling the function name makes it easier to automatically track new things.
              const eventName: any = `${InterfaceEventsEnum.APPLICATION_CONTEXT_API_BASE}.${functionDefinition[0]}`;
              trackApplicationFunctionEvent(eventName, functionArguments);

              const result = (messageContext as any)[functionDefinition[0]](...functionArguments);

              // If a promise was returned, await to return the result.
              if (isPromise(result)) {
                const promiseResult = await result;
                sendResult(message, promiseResult);
                return;
              }

              // Otherwise, return the result directly.
              sendResult(message, result);
            } catch (error) {
              sendError(message, error);
            } finally {
              contextFunctionCancelTokenSources.delete(message.id);
            }
            return;
          }

          case ApplicationMessageTypesEnum.FUNCTION_CANCEL: {
            return;
          }
          default:
            sendError(message, new Error('This should not happen.'));
        }
      } catch (error) {
        // If a function_call fails for an IO-TS error, return the error back.
        const {id, type} = event.data;
        if (error instanceof IOTSError && id && type === ApplicationMessageTypesEnum.FUNCTION_CALL) {
          sendMessageToPort({
            id,
            type: ApplicationMessageTypesEnum.FUNCTION_ERROR,
            error: String(error),
          });
          return;
        }

        reportToBugsnag(
          new ErrorWithData('Error processing application message.', {
            error,
            applicationId,
            data: event.data,
          }),
        );
      }
    },
    [
      contextFunctionCancelTokenSources,
      sendMessageToPort,
      applicationId,
      messageContexts,
      trackApplicationFunctionEvent,
    ],
  );

  useEffect(() => {
    if (!iframePort || !onPortMessage) {
      return undefined;
    }

    iframePort.addEventListener('message', onPortMessage);
    iframePort.start();

    return () => {
      iframePort.close();
      iframePort.removeEventListener('message', onPortMessage);
    };
  }, [iframePort, onPortMessage]);

  // When the context changes, send it through the entry point.
  useEffect(() => {
    if (!sendContext) {
      return;
    }
    sendContext(currentContext);
  }, [currentContext, sendContext]);

  /*
   * Iframe render.
   */

  const iframeEndpoint = useMemo(
    () => action && buildIframeEndpoint(action.endpoint, application.installationKey),
    [action, application],
  );

  if (!action || !iframeEndpoint) {
    return null;
  }

  return (
    <Iframe
      endpoint={iframeEndpoint}
      featurePolicies={buildAppIframeFeaturePolicies(entryPoint)}
      title={entryPoint.name || ''}
      sandboxPermissions={appIframePermissions}
      onMessage={onMessageFromIframe}
      isVisible
    />
  );
};

/*
 * Helpers.
 */

function buildIframeEndpoint(endpoint: string, authSecret?: string) {
  try {
    const url = new URL(endpoint);

    // Let's make sure the URL is valid before rendering the iframe.
    if (!isEntrypointUrlValid(url)) {
      return undefined;
    }

    if (authSecret) {
      return addQueryToUrl(url.toString(), {
        auth_secret: authSecret,
      });
    }
    return url.toString();
  } catch (error) {
    // This shouldn't happen, but let's not render an iFrame with an invalid URL.
    reportToBugsnag(error);
    return undefined;
  }
}
